import {
  mapRedeemBatchCreateResultDto,
  mapRedeemCodeListDto,
  mapRedeemResultDto,
  type RedeemBatchCreateResultDto,
  type RedeemCodeListDto,
  type RedeemResultDto,
} from '@/types/redeemCodes';

import type { RedeemCodesService } from '../redeemCodesService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`RedeemCodesService`(R277;契約 = BE docs/99,`dev-update-v17-1`)
 * -----------------------------------------------------------------------------
 * - `POST /api/redeem` body **只有 `code` 一欄**(BE `extra="forbid"`,多帶任何欄 422)。
 * - **不帶 `X-Idempotency-Key`**:兌換的冪等是 BE 的 per (user × code)語意
 *   (重送同一碼 = 200 + 首次結果),header 帶了也沒人消費 —— 帶著會暗示一種
 *   不存在的保護。admin 產碼**同樣沒有** BE 冪等(重按 = 再鑄一批),防重在 UI 層。
 * - 作廢的 code 可帶 dash(BE normalize),但仍 encodeURIComponent(防路徑注入)。
 */

const PATH = {
  redeem: '/api/redeem',
  adminCodes: (q?: { batchId?: string; status?: string; limit?: number; cursor?: string | null }) => {
    const params = new URLSearchParams();
    if (q?.batchId) params.set('batch_id', q.batchId);
    if (q?.status) params.set('status', q.status);
    if (q?.limit !== undefined) params.set('limit', String(q.limit));
    if (q?.cursor) params.set('cursor', q.cursor);
    const qs = params.toString();
    return `/api/admin/redeem-codes${qs ? `?${qs}` : ''}`;
  },
  adminVoid: (code: string) => `/api/admin/redeem-codes/${encodeURIComponent(code)}/void`,
} as const;

export function createHttpRedeemCodesService(config: HttpClientConfig): RedeemCodesService {
  return {
    async redeem(code) {
      const env = await httpRequestWithRefresh<RedeemResultDto>(config, 'POST', PATH.redeem, {
        code,
      });
      return mapRedeemResultDto(unwrapResult(env, 'redeemCodes.redeem'));
    },

    async adminGenerate(req) {
      const body: Record<string, unknown> = { credits: req.credits, count: req.count };
      // 選填欄不帶 undefined 上 wire(BE `extra="forbid"` 不擋 null,但缺席語意最乾淨)。
      if (req.note !== undefined && req.note !== '') body.note = req.note;
      if (req.expiresAt !== undefined && req.expiresAt !== '') body.expires_at = req.expiresAt;
      // R391 — 點數使用效期(天)。⚠️ 只在有值時帶:`null` 是 422(docs/99 D8 沒有
      // 「永久」選項),缺席才是「交給 BE 預設 30 天」。
      if (req.pointsValidDays !== undefined && Number.isFinite(req.pointsValidDays)) {
        body.points_valid_days = req.pointsValidDays;
      }
      // R391.2 — 分鐘級自訂效期(BE D9,v22-122 已對帳)。⚠️ 與 days 互斥且 BE 看
      // `model_fields_set`:minutes 有值時 days **鍵**不得出現(上面「undefined 不上
      // wire」的寫法正好守住 —— 不要改成無條件塞 key)。
      if (req.pointsValidMinutes !== undefined && Number.isFinite(req.pointsValidMinutes)) {
        body.points_valid_minutes = req.pointsValidMinutes;
      }
      const env = await httpRequestWithRefresh<RedeemBatchCreateResultDto>(
        config,
        'POST',
        PATH.adminCodes(),
        body,
      );
      return mapRedeemBatchCreateResultDto(unwrapResult(env, 'redeemCodes.adminGenerate'));
    },

    async adminList(opts) {
      const env = await httpRequestWithRefresh<RedeemCodeListDto>(
        config,
        'GET',
        PATH.adminCodes({
          batchId: opts?.batchId,
          status: opts?.status,
          limit: opts?.limit,
          cursor: opts?.cursor,
        }),
      );
      return mapRedeemCodeListDto(unwrapResult(env, 'redeemCodes.adminList'));
    },

    async adminVoid(code) {
      const env = await httpRequestWithRefresh<{ status: string }>(
        config,
        'POST',
        PATH.adminVoid(code),
        {},
      );
      // 回應只有 {code, status:"VOID"};確認 envelope 正常即可,呼叫端靠重抓清單刷新。
      unwrapResult(env, 'redeemCodes.adminVoid');
    },
  };
}
